/**
 * The Help button: the top corner of every screen, in both modes, always.
 *
 * Deliberately bigger than the burger and the bell beside it, and the only
 * corner button with a filled colour. Those two are furniture you go looking for
 * when you already know what you want; this is the one you need when you do not,
 * and it has to be findable by somebody who is not looking for anything in
 * particular — which means it cannot be the same size and weight as everything
 * else.
 *
 * It sits at the inline *start* — top-left in English, top-right in Hebrew,
 * where the reading begins. That mirroring is not a detail: the whole app flips,
 * and a button that stayed physically left would be the one thing in Hebrew that
 * had wandered off on its own.
 *
 * It is not gated by Simple or Advanced. Everything else here can be hidden from
 * somebody who does not want it; the thing you press when you are lost cannot be
 * one of them, and the person most likely to need it is exactly the person in
 * Simple mode.
 */
import { useRef } from 'react';
import { Icon } from '../components/Icon';
import { useI18n } from '../i18n';
import { useAssistant } from './AssistantState';

export function AssistantButton({ className = '' }: { className?: string }) {
  const { t } = useI18n();
  const { open, stage, openAssistant, close, walk } = useAssistant();
  const ref = useRef<HTMLButtonElement | null>(null);
  // Lit while a walkthrough is playing too — it is still the assistant talking.
  // But pressing it then *opens* the assistant rather than closing it: the walk
  // is not the thing they want to dismiss, it is the thing they want to ask
  // about. `openAssistant` ends the walk on the way in.
  const lit = open || !!walk;
  return (
    <div className={`assist-corner ${className}`}>
      <button
        ref={ref}
        type="button"
        data-assist="nav.assistant"
        /*
         * Hidden, not removed, while the big one is out.
         *
         * The microphone in the middle of the dimmed screen is meant to be read
         * as *this* button, having moved — so this one cannot still be sitting in
         * the corner while it is away. Hidden with `visibility` rather than
         * `display` so the row it sits in does not reflow around the gap, and so
         * it stays measurable for the flight home.
         *
         * It comes back the instant the answer does — which is the instant the
         * big one starts flying home — so the two overlap for the length of the
         * flight and the big one fades out onto it. That overlap is the whole
         * illusion: it lands rather than vanishing.
         */
        className={`assist-btn ${lit ? 'is-on' : ''} ${open && stage !== 'answer' ? 'is-away' : ''}`}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          /*
           * Measured at the press, and handed over.
           *
           * The overlay cannot look this up for itself: by the time it paints,
           * this button is hidden — and in Hebrew it is at the opposite corner of
           * the screen from where it is in English, so nothing can be assumed
           * about where home is. A centre and a diameter is all the flight needs.
           */
          const r = ref.current?.getBoundingClientRect();
          openAssistant(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2, size: r.width } : undefined);
        }}
        aria-label={t('assistant.open')}
        title={t('assistant.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {/*
          * A microphone, not a spark.
          *
          * The spark said "there is something clever in here", which was true and
          * not useful — it gave no hint of what pressing it would do. Now that
          * pressing it starts listening on the spot, the honest picture is the
          * one everybody already knows: somebody who sees a microphone knows to
          * talk, and does not have to be told.
          */}
        <Icon name="mic" />
      </button>
    </div>
  );
}
